import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TableSkeleton } from '../Skeleton';
import { UserPickerRow } from '../UserPickerRow';
import { ConfirmDialog } from '../ConfirmDialog';
import { User } from '../../types';
import { SelectableUser, useAffiliateUserSearch } from '../../hooks/useAffiliateUserSearch';
import { adminService, LoginApprovalRow, LoginApprovalState } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import { AlertTriangle, Check, RefreshCw, Search, ShieldCheck, UserMinus, UserPlus, Users } from 'lucide-react';
import { ADMIN_TAB_FILL, ADMIN_LIST_CAPPED } from './adminLayout';

/**
 * 로그인 승인 명단 관리 (관리자 콘솔 '로그인 승인' 탭).
 *
 * 승인제가 켜지면 이 명단에 있는 계정만 로그인할 수 있다. 명단은 계열사별로 따로 관리된다
 * (계열사마다 MSSQL이 분리돼 있어 사번이 겹칠 수 있다).
 *
 * 사번을 직접 타이핑하는 RoleManager 방식을 쓰지 않는 이유: 오타가 그대로 저장되면
 * "승인했는데 로그인이 안 된다"는 신고로 돌아온다. 직원 명부에서 골라 담는다.
 */

type ApprovalFilter = 'all' | 'approved' | 'pending';

const FILTERS: { key: ApprovalFilter; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'approved', label: '승인됨' },
  { key: 'pending', label: '미승인' },
];

interface LoginApprovalManagerProps {
  currentUser: User;
}

export const LoginApprovalManager: React.FC<LoginApprovalManagerProps> = ({ currentUser }) => {
  const { showToast } = useToast();

  // currentUserId를 넘기지 않는다 — 본인도 승인 대상이라 명부에 보여야 한다
  // (승인제를 켜려면 본인이 먼저 명단에 있어야 하고, 서버가 그것을 강제한다).
  const search = useAffiliateUserSearch({
    isActive: true,
    defaultAffiliateId: currentUser.affiliateId,
    // ⚠ 명부는 앱 전역에서 '재직 + 승인' 교집합만 보여주는데, 이 화면은 **아직 승인 안 된 사람**을
    //   찾아 승인하는 곳이다. 옵트아웃하지 않으면 승인할 대상이 목록에 나타나지 않아 기능이 죽는다.
    //   서버(/api/users/search)가 isAdmin을 재검사하므로 이 플래그만으로 권한이 생기지는 않는다.
    includeUnapproved: true,
  });

  const [state, setState] = useState<LoginApprovalState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ApprovalFilter>('all');
  const [busy, setBusy] = useState(false);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [confirm, setConfirm] = useState<
    | { kind: 'revoke'; userIds: string[]; label: string }
    | { kind: 'policy-off' }
    | null
  >(null);

  const affiliateId = search.selectedAffiliateId;

  const load = useCallback(async () => {
    if (!affiliateId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminService.getLoginApprovals(affiliateId);
      setState(data);
    } catch (err) {
      console.error('Failed to load login approvals:', err);
      setLoadError(err instanceof Error ? err.message : '승인 명단을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [affiliateId]);

  useEffect(() => {
    load();
  }, [load]);

  const approvedIds = useMemo(
    () => new Set((state?.approvals ?? []).map(a => String(a.userId))),
    [state]
  );

  /** 필터 칩까지 적용한 목록 (훅의 filteredUsers는 검색어까지만 반영한다) */
  const visibleUsers = useMemo(() => {
    if (filter === 'all') return search.filteredUsers;
    return search.filteredUsers.filter(u =>
      filter === 'approved' ? approvedIds.has(String(u.id)) : !approvedIds.has(String(u.id))
    );
  }, [search.filteredUsers, filter, approvedIds]);

  const usersByDepartment = useMemo(() => {
    const groups = new Map<string, SelectableUser[]>();
    visibleUsers.forEach(user => {
      const dept = user.depart || '미배정';
      if (!groups.has(dept)) groups.set(dept, []);
      groups.get(dept)!.push(user);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visibleUsers]);

  /**
   * 명부에 없는 승인 항목 — 퇴사 등으로 인사 시스템에서 사라진 계정이다.
   * 목록에 안 보이면 해제할 방법이 없어 승인이 영구히 남는다.
   */
  const orphanApprovals: LoginApprovalRow[] = useMemo(() => {
    if (!state) return [];
    const known = new Set(search.allUsers.map(u => String(u.id)));
    if (known.size === 0) return []; // 명부 로딩 전에는 전원을 고아로 오인하지 않는다
    return state.approvals.filter(a => !known.has(String(a.userId)));
  }, [state, search.allUsers]);

  const selectedIds = useMemo(() => search.selectedUsers.map(u => String(u.id)), [search.selectedUsers]);
  const selectedPending = useMemo(() => selectedIds.filter(id => !approvedIds.has(id)), [selectedIds, approvedIds]);
  const selectedApproved = useMemo(() => selectedIds.filter(id => approvedIds.has(id)), [selectedIds, approvedIds]);

  const handleGrant = async () => {
    if (!affiliateId || selectedPending.length === 0 || busy) return;
    const byId = new Map(search.allUsers.map(u => [String(u.id), u]));
    setBusy(true);
    try {
      const res = await adminService.grantLoginApprovals({
        affiliateId,
        users: selectedPending.map(id => {
          const u = byId.get(id);
          return { userId: id, userid: u?.userid, name: u?.username, depart: u?.depart };
        }),
      });
      search.reset();
      await load();
      showToast({ type: 'success', message: `${res.added}명을 승인했습니다.` });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '승인하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setBusy(false);
    }
  };

  const performRevoke = async (userIds: string[]) => {
    if (!affiliateId || userIds.length === 0) return;
    setBusy(true);
    try {
      const results = await Promise.allSettled(
        userIds.map(id => adminService.revokeLoginApproval(affiliateId, id))
      );
      const failed = results.filter(r => r.status === 'rejected');
      search.reset();
      await load();
      if (failed.length === 0) {
        showToast({ type: 'success', message: `${userIds.length}명의 승인을 해제했습니다.` });
      } else {
        const reason = (failed[0] as PromiseRejectedResult).reason;
        showToast({
          type: 'error',
          message: reason?.message || `${failed.length}명은 승인을 해제하지 못했습니다.`,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const askRevoke = (userIds: string[], label: string) => {
    if (userIds.length === 0) return;
    // 서버도 막지만, 눌러 보고 실패하는 것보다 미리 안내하는 편이 낫다
    const withoutSelf = userIds.filter(id => id !== String(currentUser.id));
    if (withoutSelf.length === 0) {
      showToast({ type: 'info', message: '자신의 승인은 해제할 수 없습니다.' });
      return;
    }
    if (withoutSelf.length < userIds.length) {
      showToast({ type: 'info', message: '자신을 제외하고 해제합니다.' });
    }
    setConfirm({ kind: 'revoke', userIds: withoutSelf, label });
  };

  const handleSeedActive = async () => {
    if (!affiliateId || busy) return;
    setBusy(true);
    try {
      const res = await adminService.seedActiveLoginApprovals(affiliateId);
      await load();
      showToast({
        type: 'success',
        message: res.added > 0
          ? `접속 이력이 있는 ${res.added}명을 승인했습니다.`
          : '새로 승인할 접속 이력자가 없습니다.',
      });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '접속 이력자를 승인하지 못했습니다.' });
    } finally {
      setBusy(false);
    }
  };

  const applyPolicy = async (enabled: boolean) => {
    if (policyBusy) return;
    setPolicyBusy(true);
    try {
      const next = await adminService.updateLoginApprovalPolicy(enabled);
      setState(prev => (prev ? { ...prev, ...next } : prev));
      showToast({
        type: 'success',
        message: enabled
          ? '로그인 승인제를 켰습니다. 이제 승인된 계정만 로그인할 수 있습니다.'
          : '로그인 승인제를 껐습니다.',
      });
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '승인제 설정을 저장하지 못했습니다.' });
    } finally {
      setPolicyBusy(false);
    }
  };

  const enabled = state?.enabled === true;
  const totalCount = search.allUsers.length;
  const searching = search.searchQuery.trim().length > 0 || filter !== 'all';

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* 승인제 스위치 */}
      <div className="mb-4 p-4 bg-white border border-slate-200 rounded-xl">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className={enabled ? 'text-brand-600' : 'text-slate-400'} aria-hidden="true" />
              <span className="text-sm font-medium text-slate-800">로그인 승인제</span>
              <span
                className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                  enabled ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {enabled ? '켜짐' : '꺼짐'}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {enabled
                ? '승인된 계정만 로그인할 수 있습니다. 명단에 없으면 아이디와 비밀번호가 맞아도 거부됩니다.'
                : '인트라넷 계정이 있는 전 직원이 로그인할 수 있습니다. 명단은 미리 준비해 둘 수 있습니다.'}
            </p>
            {state?.updatedAt && (
              <p className="mt-0.5 text-xs text-slate-400">
                마지막 변경: {state.updatedByName} · {new Date(state.updatedAt).toLocaleString('ko-KR')}
              </p>
            )}
          </div>
          {state?.canManageAllAffiliates && !state?.envKillSwitch && (
            <button
              type="button"
              onClick={() => (enabled ? setConfirm({ kind: 'policy-off' }) : applyPolicy(true))}
              disabled={policyBusy}
              className={`shrink-0 px-3 py-1.5 text-sm rounded-lg transition-colors disabled:opacity-50 ${
                enabled
                  ? 'bg-white border border-brand-600 text-brand-600 hover:bg-brand-50'
                  : 'bg-brand-600 text-white hover:bg-brand-700'
              }`}
            >
              {enabled ? '끄기' : '켜기'}
            </button>
          )}
        </div>

        {state?.envKillSwitch && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              서버 설정(LOGIN_APPROVAL_ENABLED=false)으로 승인제가 꺼져 있습니다. 이 화면에서는 켤 수 없습니다.
            </span>
          </div>
        )}
        {!state?.canManageAllAffiliates && !state?.envKillSwitch && (
          <p className="mt-3 text-xs text-slate-400">승인제를 켜고 끄는 것은 Super Admin만 할 수 있습니다.</p>
        )}
      </div>

      {/* 액션 바 */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          {state?.canManageAllAffiliates && search.showAffiliateSelector && (
            <select
              value={affiliateId}
              onChange={e => search.setSelectedAffiliateId(e.target.value)}
              aria-label="계열사 선택"
              className="px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {search.affiliates.map(a => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          )}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search.searchQuery}
              onChange={e => search.setSearchQuery(e.target.value)}
              aria-label="직원 검색"
              placeholder="이름이나 부서로 찾기"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSeedActive}
            disabled={busy || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            title="이미 앱에 접속한 적이 있는 사용자를 한 번에 승인합니다"
          >
            <Users size={14} aria-hidden="true" />
            접속 이력자 승인
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
            새로고침
          </button>
        </div>
      </div>

      {/* 필터 + 카운트 + 일괄 액션 */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          {FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                filter === f.key
                  ? 'bg-brand-100 border-brand-600 text-brand-700 font-medium'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="text-sm text-slate-500 ml-1">
            {searching
              ? `${visibleUsers.length}명 (전체 ${totalCount}명)`
              : `승인 ${approvedIds.size}명 / 전체 ${totalCount}명`}
          </span>
        </div>
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">{selectedIds.length}명 선택</span>
            <button
              onClick={handleGrant}
              disabled={busy || selectedPending.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
            >
              <UserPlus size={14} aria-hidden="true" />
              승인 {selectedPending.length > 0 && `(${selectedPending.length})`}
            </button>
            <button
              onClick={() => askRevoke(selectedApproved, `선택한 ${selectedApproved.length}명`)}
              disabled={busy || selectedApproved.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-brand-600 text-brand-600 rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50"
            >
              <UserMinus size={14} aria-hidden="true" />
              해제 {selectedApproved.length > 0 && `(${selectedApproved.length})`}
            </button>
          </div>
        )}
      </div>

      {/* 에러 배너 */}
      {(loadError || search.error) && (
        <div className="mb-3 px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded border border-brand-200 flex items-center justify-between">
          <span>{loadError || search.error}</span>
          <button onClick={load} className="text-brand-700 underline text-xs ml-2 shrink-0">
            다시 시도
          </button>
        </div>
      )}

      {/* 직원 명부 */}
      {(loading || search.loading) && search.allUsers.length === 0 ? (
        <TableSkeleton rows={6} cols={3} />
      ) : visibleUsers.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          {searching ? '조건에 맞는 직원이 없습니다' : '직원 명부를 불러오지 못했습니다'}
        </div>
      ) : (
        // 테두리 상자가 ADMIN_LIST_FILL의 역할(내용만큼 자라다 남는 높이에서 멈춤, 하한 12rem)을 맡고
        // 스크롤은 안쪽이 한다 — 부서 sticky 머리글이 테두리 안쪽 맨 위에 붙어야 하기 때문
        <div className="min-h-[12rem] flex flex-col border border-slate-200 rounded-xl overflow-hidden">
          <div className="min-h-0 overflow-y-auto divide-y divide-slate-100">
            {usersByDepartment.map(([dept, users]) => (
              <div key={dept}>
                <div className="sticky top-0 z-10 px-3 py-1.5 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500">
                  {dept} ({users.length})
                </div>
                {users.map(user => {
                  const isApproved = approvedIds.has(String(user.id));
                  return (
                    <UserPickerRow
                      key={String(user.id)}
                      user={user}
                      onlineUsers={[]}
                      showPresence={false}
                      onToggle={search.toggleUser}
                      badge={
                        isApproved ? (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium bg-brand-100 text-brand-700">
                            <Check size={11} aria-hidden="true" />
                            승인
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-500">미승인</span>
                        )
                      }
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 명부에 없는 승인 (퇴사자 등) */}
      {orphanApprovals.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-amber-500" aria-hidden="true" />
            <span className="text-sm font-medium text-slate-700">
              직원 명부에 없는 승인 {orphanApprovals.length}건
            </span>
          </div>
          <p className="mb-2 text-xs text-slate-500">
            인사 시스템에서 사라진 계정입니다. 필요 없으면 승인을 해제하세요.
          </p>
          <div className={`space-y-2 ${ADMIN_LIST_CAPPED}`}>
            {orphanApprovals.map(row => (
              <div
                key={row.userId}
                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {row.userName || '(이름 없음)'}
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    사번: {row.userId}
                    {row.userid && (
                      <>
                        <span className="mx-1">·</span>
                        아이디: {row.userid}
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => askRevoke([row.userId], row.userName || row.userId)}
                  disabled={busy}
                  className="shrink-0 px-3 py-1.5 text-sm bg-white border border-brand-600 text-brand-600 rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50"
                >
                  해제
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirm?.kind === 'revoke'}
        title="로그인 승인 해제"
        message={
          confirm?.kind === 'revoke'
            ? `${confirm.label}의 로그인 승인을 해제할까요?${
                enabled ? ' 접속 중이면 즉시 로그아웃되고 다시 로그인할 수 없습니다.' : ''
              }`
            : ''
        }
        confirmLabel="해제"
        danger
        onConfirm={() => {
          if (confirm?.kind === 'revoke') performRevoke(confirm.userIds);
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        isOpen={confirm?.kind === 'policy-off'}
        title="로그인 승인제 끄기"
        message="승인제를 끌까요? 인트라넷 계정이 있는 전 직원이 다시 로그인할 수 있게 됩니다."
        confirmLabel="끄기"
        danger
        onConfirm={() => {
          applyPolicy(false);
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};
